/**
 * Shared client-side helper for calling our own /api/* routes.
 *
 * Why this exists
 * ---------------
 * Every place that calls a Next.js API route from the browser needs the
 * same plumbing: the JWT in `Authorization: Bearer <token>`, an
 * `X-Idempotency-Key` for write retries, an `X-Correlation-Id` so the
 * browser console can be matched against server logs, error parsing
 * that turns 4xx/5xx into typed exceptions. Hand-rolling that with
 * `fetch()` repeatedly is how callers ended up missing the auth header
 * (see F7 subscriptions/create 401 incident on 2026-05-02).
 *
 * Usage:
 *   import { apiPost } from "@/lib/api-client"
 *   const sub = await apiPost<{ id: string }>("/api/subscriptions/create", values)
 *
 * On error throws one of:
 *   - ApiAuthError       (no session, or server returned 401)
 *   - ApiForbiddenError  (server returned 403)
 *   - ApiValidationError (server returned 400 with Zod issues)
 *   - ApiConflictError   (server returned 409)
 *   - ApiRateLimitError  (server returned 429)
 *   - ApiServerError     (any other non-2xx; carries status + body)
 *
 * See `src/app/api/CLAUDE.md` for the server-side counterpart and the
 * full list of headers each side honours.
 */

import { announceAccessRefused } from "@/lib/access-events";
import { nhost } from "@/lib/nhost";

export class ApiAuthError extends Error {
  constructor(
    message: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiAuthError";
  }
}

export class ApiForbiddenError extends Error {
  constructor(
    message: string,
    readonly correlationId?: string,
    /**
     * The body's machine-readable `code` (e.g. `password_change_required`,
     * `wrong_current_password`, `no_membership`), so a caller can branch on it, not on prose.
     */
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiForbiddenError";
  }
}

export class ApiValidationError extends Error {
  constructor(
    message: string,
    readonly issues: unknown,
    readonly correlationId?: string,
    /**
     * Full response body, e.g. `{ code, earliest, … }` for structured
     * 400s that carry more than Zod issues. Read named fields off this
     * to recover the discriminator (`code`) and any companion data.
     */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiValidationError";
  }
}

export class ApiConflictError extends Error {
  constructor(
    message: string,
    readonly correlationId?: string,
    /**
     * Full response body, so a caller can branch on `code` (ticket 215).
     *
     * IT WAS MISSING, AND THAT WAS A REAL GAP RATHER THAN A TIDY-UP. `ApiValidationError` and
     * `ApiServerError` both carry the body, so every 400, 404, 422 and 500 reaches a caller with
     * its machine-readable `code` intact — and a 409 did not. §7 says clients branch on `code`
     * and never on message text, which a 409 made impossible: "a reason with that code already
     * exists" and "that list changed while you were reordering it" are different sentences a
     * screen has to tell apart, and prose was the only thing left to tell them apart by.
     */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiConflictError";
  }
}

export class ApiRateLimitError extends Error {
  constructor(
    message: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiRateLimitError";
  }
}

export class ApiServerError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiServerError";
  }
}

/**
 * True for a server failure answered by `respondUnexpectedError` (`code: "unexpected_error"`):
 * the message is a fixed English sentence, so a screen should show its own translated
 * "could not …" text instead of `err.message` (WP6a).
 */
export function isUnexpectedServerError(err: unknown): boolean {
  return (
    err instanceof ApiServerError &&
    (err.body as { code?: unknown } | null)?.code === "unexpected_error"
  );
}

/**
 * The JSON body a refused request answered with, or null when the error carries none. Which
 * class carries it depends on the status: a 400 arrives as `ApiValidationError`, a 409 as
 * `ApiConflictError`, anything else (402, 404, 422, 500) as `ApiServerError`. A 401, 403 or 429
 * carries no body.
 */
export function apiErrorBody(err: unknown): Record<string, unknown> | null {
  const body =
    err instanceof ApiServerError ||
    err instanceof ApiValidationError ||
    err instanceof ApiConflictError
      ? err.body
      : null;
  if (!body || typeof body !== "object") return null;
  return body as Record<string, unknown>;
}

/**
 * The machine-readable `code` of a refusal, or null when the answer carries none. A screen
 * picks its sentence from this, never from the server's own English text.
 */
export function apiErrorCode(err: unknown): string | null {
  const code = apiErrorBody(err)?.code;
  return typeof code === "string" ? code : null;
}

interface ApiOptions {
  /**
   * Override the auto-generated idempotency key. Pass the SAME value
   * across retries of a logically-identical operation so the server
   * returns the original result rather than re-executing.
   */
  idempotencyKey?: string;
  /**
   * Optional explicit correlation id. Defaults to a fresh uuid.
   */
  correlationId?: string;
  /**
   * AbortSignal for caller-driven cancellation.
   */
  signal?: AbortSignal;
}

interface SessionAccessor {
  getUserSession(): { accessToken?: string | null } | null;
}

/**
 * Default session accessor — points at the real Nhost client. Tests can
 * inject a fake via the internal `__setSessionAccessorForTesting` hook.
 */
let sessionAccessor: SessionAccessor = nhost as unknown as SessionAccessor;

/** @internal — test hook only */
export function __setSessionAccessorForTesting(
  next: SessionAccessor | null,
): void {
  sessionAccessor = next ?? (nhost as unknown as SessionAccessor);
}

interface FetchLike {
  (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

let fetchImpl: FetchLike = (input, init) => fetch(input, init);

/** @internal — test hook only */
export function __setFetchForTesting(next: FetchLike | null): void {
  fetchImpl = next ?? ((input, init) => fetch(input, init));
}

function newUuid(): string {
  // crypto.randomUUID is available in all browsers we target + Node 19+
  // (which jsdom uses). Falling back to a non-crypto uuid is intentionally
  // not provided — we want to fail loudly if the runtime lacks it.
  return crypto.randomUUID();
}

interface BuiltRequest {
  headers: Headers;
  correlationId: string;
  idempotencyKey: string;
}

function buildRequest(
  method: string,
  opts: ApiOptions | undefined,
  hasBody: boolean,
): BuiltRequest {
  const session = sessionAccessor.getUserSession();
  if (!session?.accessToken) {
    throw new ApiAuthError(
      "Not signed in — refresh and try again",
      opts?.correlationId,
    );
  }

  const correlationId = opts?.correlationId ?? newUuid();
  const idempotencyKey = opts?.idempotencyKey ?? newUuid();
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${session.accessToken}`);
  headers.set("X-Correlation-Id", correlationId);
  if (method !== "GET") {
    headers.set("X-Idempotency-Key", idempotencyKey);
  }
  if (hasBody) {
    headers.set("Content-Type", "application/json");
  }
  // Tag method-mutations with idempotency even on PUT/DELETE — server can
  // ignore for idempotent verbs but the header costs nothing.
  return { headers, correlationId, idempotencyKey };
}

interface ApiErrorBody {
  error?: string;
  issues?: unknown;
  [k: string]: unknown;
}

async function readErrorBody(res: Response): Promise<ApiErrorBody> {
  try {
    return (await res.json()) as ApiErrorBody;
  } catch {
    return {};
  }
}

function throwForStatus(
  res: Response,
  body: ApiErrorBody,
  correlationId: string,
): never {
  const responseCorrelation =
    res.headers.get("x-correlation-id") ?? correlationId;
  const message = body.error ?? `Request failed (${res.status})`;
  const code = typeof body.code === "string" ? body.code : undefined;
  switch (res.status) {
    case 401:
      // The dashboard's session monitor decides whether the person's access has ended.
      announceAccessRefused({ status: 401, code: code ?? null });
      throw new ApiAuthError(message, responseCorrelation);
    case 403:
      announceAccessRefused({ status: 403, code: code ?? null });
      throw new ApiForbiddenError(message, responseCorrelation, code);
    case 400:
      throw new ApiValidationError(
        message,
        body.issues ?? null,
        responseCorrelation,
        body as Record<string, unknown>,
      );
    case 409:
      throw new ApiConflictError(
        message,
        responseCorrelation,
        body as Record<string, unknown>,
      );
    case 429:
      throw new ApiRateLimitError(message, responseCorrelation);
    default:
      throw new ApiServerError(message, res.status, body, responseCorrelation);
  }
}

async function parseSuccess<T>(res: Response): Promise<T> {
  // 204 No Content → return undefined cast to T; callers that expect a
  // payload should not call no-content endpoints.
  if (res.status === 204) return undefined as unknown as T;
  // Some endpoints respond with empty body on 200 — guard against that too.
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiServerError(
      "Server returned a non-JSON success response",
      res.status,
      text,
    );
  }
}

/**
 * POST a JSON body to one of our API routes. Always sends the JWT,
 * a fresh `X-Idempotency-Key`, and a `X-Correlation-Id`.
 */
export async function apiPost<T = unknown>(
  url: string,
  body: unknown,
  opts?: ApiOptions,
): Promise<T> {
  const { headers, correlationId } = buildRequest("POST", opts, true);
  const res = await fetchImpl(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body ?? {}),
    signal: opts?.signal,
  });
  if (!res.ok) throwForStatus(res, await readErrorBody(res), correlationId);
  return parseSuccess<T>(res);
}

/**
 * POST a FILE to one of our API routes, as `multipart/form-data` (#227).
 *
 * Same auth, idempotency and correlation handling as {@link apiPost}; the only difference is
 * that the body is a `FormData` and **no `Content-Type` header is set**. That omission is the
 * entire point of this function existing: `fetch` derives the header from the FormData so it
 * can append the multipart boundary token, and a hand-set `content-type` produces a body the
 * server cannot parse — with no error until the parse fails.
 *
 * Added for the inbox composer's attachments. The alternative was a hand-rolled `fetch` at the
 * call site, which `src/app/api/CLAUDE.md` forbids for good reason: it would have had to
 * re-implement the JWT header, the idempotency key and the typed error mapping, and the
 * idempotency key is what stops a double-click reaching Meta twice.
 */
export async function apiPostForm<T = unknown>(
  url: string,
  form: FormData,
  opts?: ApiOptions,
): Promise<T> {
  // `hasBody: false` — see above. There IS a body; what there must not be is our own
  // content-type header.
  const { headers, correlationId } = buildRequest("POST", opts, false);
  const res = await fetchImpl(url, {
    method: "POST",
    headers,
    body: form,
    signal: opts?.signal,
  });
  if (!res.ok) throwForStatus(res, await readErrorBody(res), correlationId);
  return parseSuccess<T>(res);
}

/**
 * POST a JSON body and receive a FILE back — same auth, idempotency and correlation
 * handling as `apiPost`, but the success body is a Blob rather than JSON.
 *
 * Added for the audited CRM lead export (#97), which returns `text/csv`. `apiPost` parses
 * every success body as JSON and would reject a perfectly good CSV as "non-JSON success
 * response". The alternative was a hand-rolled `fetch` at the call site, which is exactly
 * what `prefer-api-client.sh` exists to prevent — and it would have had to re-implement the
 * bearer token, which is the bug class that hook was written for.
 *
 * Errors are still JSON, so the failure path is shared with every other helper here: the
 * caller gets the same typed `ApiForbiddenError` / `ApiValidationError` it would from a
 * JSON route.
 *
 * The response headers come back alongside the blob because a file response carries
 * metadata worth reading — the export uses `X-Export-Row-Count` to tell the user how many
 * rows they actually got.
 */
export async function apiPostBlob(
  url: string,
  body: unknown,
  opts?: ApiOptions,
): Promise<{ blob: Blob; headers: Headers }> {
  const { headers, correlationId } = buildRequest("POST", opts, true);
  const res = await fetchImpl(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body ?? {}),
    signal: opts?.signal,
  });
  if (!res.ok) throwForStatus(res, await readErrorBody(res), correlationId);
  return { blob: await res.blob(), headers: res.headers };
}

/**
 * GET from one of our API routes with the JWT and a correlation id.
 */
export async function apiGet<T = unknown>(
  url: string,
  opts?: ApiOptions,
): Promise<T> {
  const { headers, correlationId } = buildRequest("GET", opts, false);
  const res = await fetchImpl(url, {
    method: "GET",
    headers,
    signal: opts?.signal,
  });
  if (!res.ok) throwForStatus(res, await readErrorBody(res), correlationId);
  return parseSuccess<T>(res);
}

/**
 * PUT a JSON body to one of our API routes.
 */
export async function apiPut<T = unknown>(
  url: string,
  body: unknown,
  opts?: ApiOptions,
): Promise<T> {
  const { headers, correlationId } = buildRequest("PUT", opts, true);
  const res = await fetchImpl(url, {
    method: "PUT",
    headers,
    body: JSON.stringify(body ?? {}),
    signal: opts?.signal,
  });
  if (!res.ok) throwForStatus(res, await readErrorBody(res), correlationId);
  return parseSuccess<T>(res);
}

/**
 * PATCH a JSON body to one of our API routes.
 */
export async function apiPatch<T = unknown>(
  url: string,
  body: unknown,
  opts?: ApiOptions,
): Promise<T> {
  const { headers, correlationId } = buildRequest("PATCH", opts, true);
  const res = await fetchImpl(url, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body ?? {}),
    signal: opts?.signal,
  });
  if (!res.ok) throwForStatus(res, await readErrorBody(res), correlationId);
  return parseSuccess<T>(res);
}

/**
 * DELETE one of our API resources.
 */
export async function apiDelete<T = unknown>(
  url: string,
  opts?: ApiOptions,
): Promise<T> {
  const { headers, correlationId } = buildRequest("DELETE", opts, false);
  const res = await fetchImpl(url, {
    method: "DELETE",
    headers,
    signal: opts?.signal,
  });
  if (!res.ok) throwForStatus(res, await readErrorBody(res), correlationId);
  return parseSuccess<T>(res);
}
